import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

export default function Pillars() {
    const t = useT();
    const p = t.home.pillars;

    // Trigger connector-line draw when the row enters the viewport.
    const rowRef = useRef(null);
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        const el = rowRef.current;
        if (!el) return;
        if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setDrawn(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setDrawn(true);
                        io.disconnect();
                    }
                });
            },
            { threshold: 0.25, rootMargin: "0px 0px -40px 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <section
            data-testid="pillars"
            id="approach"
            className="section-x section-y"
            style={{ background: "var(--paper)" }}
        >
            <div>
                <div className="flex items-center gap-3">
                    <div style={{ width: 32, height: 1, background: "var(--orange)" }} />
                    <p className="font-mono" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>
                        {p.eyebrow}
                    </p>
                </div>
                <h2
                    className="font-display mt-6"
                    style={{
                        fontSize: "clamp(32px, 3.4vw, 50px)",
                        lineHeight: 1.12,
                        letterSpacing: "-0.02em",
                        fontWeight: 500,
                        color: "var(--navy)",
                        maxWidth: 880,
                    }}
                >
                    {p.titleLine1}<br />
                    <em style={{ fontStyle: "normal", color: "var(--orange)", fontWeight: 500 }}>{p.titleLine2}</em>
                </h2>
                <div className="mt-8" style={{ width: 56, height: 2, background: "var(--navy)" }} />
                <p className="font-serif mt-6 lg:whitespace-nowrap" style={{ fontSize: 17, lineHeight: 1.65, color: "var(--steel)" }}>
                    {p.subtitle}
                </p>
            </div>

            <div className="mt-16">
                <div ref={rowRef} className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
                    {/* Connector line — draws from left to right on scroll-in */}
                    <div
                        aria-hidden="true"
                        className="hidden md:block"
                        style={{
                            position: "absolute",
                            top: 36,
                            left: "16.66%",
                            right: "16.66%",
                            height: 1,
                            background: "var(--orange)",
                            opacity: 0.55,
                            transformOrigin: "left center",
                            transform: drawn ? "scaleX(1)" : "scaleX(0)",
                            transition: "transform 1200ms cubic-bezier(0.65, 0, 0.35, 1) 200ms",
                            zIndex: 0,
                        }}
                    />
                    {p.items.map((item, i) => (
                        <Reveal
                            key={i}
                            delay={i * 140}
                            as="div"
                            className="relative flex flex-col items-center text-center pillar-card"
                            style={{ zIndex: 1 }}
                        >
                            <div data-testid={`pillar-${i}`} className="pillar-inner">
                                <div className="pillar-circle">
                                    <span
                                        className="font-display"
                                        style={{
                                            fontSize: 30,
                                            lineHeight: 1,
                                            color: "#fff",
                                            fontWeight: 600,
                                            letterSpacing: "-0.02em",
                                        }}
                                    >
                                        {i + 1}
                                    </span>
                                </div>
                                <h3
                                    className="font-display mt-7"
                                    style={{
                                        fontSize: "clamp(20px, 1.6vw, 24px)",
                                        lineHeight: 1.2,
                                        color: "var(--navy)",
                                        fontWeight: 600,
                                        letterSpacing: "-0.01em",
                                        maxWidth: 320,
                                    }}
                                >
                                    {item.title}
                                </h3>
                                <div className="pillar-rule mt-4 mx-auto" />
                                <p
                                    className="font-serif mt-5"
                                    style={{
                                        fontSize: 15.5,
                                        lineHeight: 1.65,
                                        color: "var(--ink)",
                                        maxWidth: 320,
                                    }}
                                >
                                    {item.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
